import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Redirect, useLocation, useParams} from 'react-router-dom';
import {Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {canOpenReportBuilder} from './reportBuilderGate';
import {ReportBuilderProvider, ReportIntentPayload} from './ReportBuilderContext';
import {ReportBuilderSurface} from './ReportBuilderSurface';
import {validateReportDefinition} from './contracts/validator';
import {ReportDataSource} from './data/ReportDataSource';
import {HttpReportDataSource} from './data/HttpReportDataSource';
import type {ReportTaxonomy} from './contracts/report-taxonomy.types';
import {useReportTaxonomy} from './useReportTaxonomy';

/** Production data source: the real `POST /report/data/fetch` transport. A module singleton so a
 * re-render does not churn a new instance (keeps the provider's memoised source stable).
 * Tests/Storybook inject a `MockReportDataSource` via the `dataSource` prop instead. */
const httpReportDataSource: ReportDataSource = new HttpReportDataSource();

/** Report intent handed to the page through react-router location state when the chat card's "View"
 * navigates here. The intent is ephemeral (agent-generated, not persisted), so a refresh or direct
 * hit carries no state and the page redirects away. */
interface ReportBuilderLocationState {
	initialConfig?: ReportIntentPayload;
	/** Logged-in person id, scopes the per-user layout overlay. Falls back to the provider's 'me'. */
	userId?: string;
	/** Task-report taxonomy fetched by the chat surface when the intent arrived. Optional — a
	 * failed/slow fetch leaves this undefined and the provider falls back to its own null default,
	 * which disables condition editing until a taxonomy fetch elsewhere supplies one. */
	taxonomy?: ReportTaxonomy;
}

/** Fills the routed content area as a normal page (in-flow, not a portalled full-viewport takeover).
 * The surface itself scrolls. */
const Surface = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: auto;
`;

const ErrorPanel = styled.div`
	padding: 24px;
`;

/** Keys the report-definition contract allows. A saved report's definition arrives with storage fields
 * alongside them (id, owner, company, audit stamps), and the contract validator rejects unknown keys,
 * so the stored shape is narrowed to the contract before it is validated or handed to the provider. */
const DEFINITION_KEYS = [
	'schemaVersion',
	'reportType',
	'name',
	'startDate',
	'endDate',
	'grouping',
	'fields',
	'filter',
	'layout',
	'sort',
] as const;

/** Narrows a stored report definition to the contract shape. `groupings` is the stored plural spelling
 * of the contract's `grouping`; empty slots are dropped, since storage pads them but the contract
 * forbids them. */
export function toReportDefinition(stored: Record<string, unknown>): ReportIntentPayload['definition'] {
	const definition: Record<string, unknown> = {};
	for (const key of DEFINITION_KEYS) {
		if (stored[key] !== undefined && stored[key] !== null) {
			definition[key] = stored[key];
		}
	}
	// A column that was never resized is stored with a null width; the contract has no null, it has an
	// absent width, so the key is dropped rather than carried through as null.
	if (Array.isArray(definition.layout)) {
		definition.layout = (definition.layout as Array<Record<string, unknown>>).map(column =>
			typeof column?.width === 'number' ? {field: column.field, width: column.width} : {field: column?.field}
		);
	}

	// Storage leaves an unused filter group null and types condition values loosely; the contract wants the
	// key absent and the values as strings. It also returns no filter at all for a report with no filter
	// row, while the contract requires one — so an absent filter becomes an empty filter rather than being
	// left out and failing validation.
	const filter = (definition.filter ?? {}) as Record<string, unknown>;
	{
		const conditions = Array.isArray(filter.filterConditions) ? filter.filterConditions : [];
		definition.filter = {
			type: filter.type ?? 'SIMPLE_AND',
			...(typeof filter.filterGroup === 'string' && filter.filterGroup !== '' && {filterGroup: filter.filterGroup}),
			filterConditions: (conditions as Array<Record<string, unknown>>).map(condition => ({
				field: condition.field,
				operator: condition.operator,
				context: condition.context,
				values: (Array.isArray(condition.values) ? condition.values : []).map(value => String(value)),
			})),
		};
	}

	const grouping = (definition.grouping ?? stored.groupings) as unknown[] | undefined;
	if (Array.isArray(grouping)) {
		const populated = grouping.filter(token => typeof token === 'string' && token !== '');
		if (populated.length > 0) {
			definition.grouping = populated;
		} else {
			delete definition.grouping;
		}
	}
	return definition as unknown as ReportIntentPayload['definition'];
}

export interface ReportBuilderPageProps {
	/** Overridable for tests/Storybook; production defaults to the live `HttpReportDataSource`. */
	dataSource?: ReportDataSource;
}

interface ReportBuilderViewProps {
	initialConfig: ReportIntentPayload;
	userId?: string;
	/** Taxonomy the chat surface may have passed in router state; used only to seed the fetch so
	 * editing works instantly when present. The view fetches its own regardless. */
	taxonomySeed?: ReportTaxonomy;
	dataSource: ReportDataSource;
}

/** Mounts the provider + surface once the page guards have passed. Kept as its own component so the
 * taxonomy fetch hook runs unconditionally (never behind the page's early returns), and so the
 * builder resolves its own taxonomy rather than depending on the ephemeral chat handoff — a
 * missing/slow/failed handoff no longer hides the conditions editor. */
function ReportBuilderView({initialConfig, userId, taxonomySeed, dataSource}: ReportBuilderViewProps): JSX.Element {
	const taxonomy = useReportTaxonomy(taxonomySeed);
	return (
		// Key by draftId so a refined create_report_intent (new draftId) remounts the provider with
		// the new definition. The provider seeds definition/grouping/tableState once on mount with no
		// reset-on-payload, so without a changing key a refinement would keep the previous draft.
		// Falls back to a stable key for non-intent opens.
		<ReportBuilderProvider
			key={initialConfig.draftId ?? 'report-builder'}
			payload={initialConfig}
			role={'editor'}
			dataSource={dataSource}
			userId={userId}
			taxonomy={taxonomy}
		>
			<ReportBuilderSurface />
		</ReportBuilderProvider>
	);
}

type LoadState = {status: 'loading'} | {status: 'loaded'; definition: ReportIntentPayload['definition']} | {status: 'failed'};

/** Re-opens a report that was saved from the builder, loading its definition by id. The report intent
 * the chat hands over is ephemeral, so a saved report has no router state to restore from and its
 * definition is read back from storage instead. */
function SavedReportBuilder({savedReportId, dataSource}: {savedReportId: string; dataSource: ReportDataSource}): JSX.Element {
	const {formatMessage} = useIntl();
	const [state, setState] = useState<LoadState>({status: 'loading'});

	useEffect(() => {
		let cancelled = false;
		setState({status: 'loading'});
		void dataSource.loadDefinition(savedReportId).then(
			stored => {
				if (cancelled) {
					return;
				}
				setState({status: 'loaded', definition: toReportDefinition(stored as unknown as Record<string, unknown>)});
			},
			() => {
				if (!cancelled) {
					setState({status: 'failed'});
				}
			}
		);
		return () => {
			cancelled = true;
		};
	}, [savedReportId, dataSource]);

	if (state.status === 'loading') {
		return <ErrorPanel data-cy="ai-report-builder-loading" />;
	}

	if (state.status === 'failed') {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Text>{formatMessage({id: 'ai_report_builder.error_title'})}</Text>
				<Text>{formatMessage({id: 'ai_report_builder.error_load_failed'})}</Text>
			</ErrorPanel>
		);
	}

	const {valid, errors} = validateReportDefinition(state.definition);
	if (!valid) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Text>{formatMessage({id: 'ai_report_builder.error_title'})}</Text>
				<Text>{formatMessage({id: 'ai_report_builder.error_invalid_definition'})}</Text>
				<Text>{errors.join(', ')}</Text>
			</ErrorPanel>
		);
	}

	// Keyed by report id so navigating between two saved reports remounts the provider, which seeds its
	// working state once on mount.
	return (
		<ReportBuilderView
			key={savedReportId}
			initialConfig={{definition: state.definition, draftId: savedReportId}}
			dataSource={dataSource}
		/>
	);
}

/** The AI report builder rendered as a routed view (path `/report-builder`), mounted in the app's
 * main content region beside the primary navigation. Reached only from the chat card's "View", which
 * navigates here with the report intent in location state. Security-critical gate: redirects to `/`
 * unless `canOpenReportBuilder()` passes AND an intent is present in location state. Guards a
 * malformed `initialConfig.definition` (e.g. an upstream agent response that fails schema
 * validation) by rendering an error block instead of crashing the surface/provider. */
export function ReportBuilderPage({dataSource = httpReportDataSource}: ReportBuilderPageProps): JSX.Element {
	const {formatMessage} = useIntl();
	const location = useLocation<ReportBuilderLocationState>();
	const {reportId} = useParams<{reportId?: string}>();
	const state = location.state;

	if (!canOpenReportBuilder()) {
		return <Redirect to="/" />;
	}

	// Re-opening a saved report: the definition comes from storage, not from router state.
	if (reportId) {
		return (
			<Surface data-cy="ai-report-builder-page">
				<SavedReportBuilder savedReportId={reportId} dataSource={dataSource} />
			</Surface>
		);
	}

	if (!state || !state.initialConfig) {
		return <Redirect to="/" />;
	}

	const {initialConfig, userId, taxonomy} = state;
	const {valid, errors} = validateReportDefinition(initialConfig.definition);

	if (!valid) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Text>{formatMessage({id: 'ai_report_builder.error_title'})}</Text>
				<Text>{formatMessage({id: 'ai_report_builder.error_invalid_definition'})}</Text>
				<Text>{errors.join(', ')}</Text>
			</ErrorPanel>
		);
	}

	return (
		<Surface data-cy="ai-report-builder-page">
			<ReportBuilderView initialConfig={initialConfig} userId={userId} taxonomySeed={taxonomy} dataSource={dataSource} />
		</Surface>
	);
}

export default ReportBuilderPage;
